/**
 * Dual-handle price slider for the collection / search facets.
 *
 * Replaces Dawn's "From / To" text boxes with a draggable two-handle track, the
 * way the old site had it. The text boxes stay in the DOM untouched - they are
 * the real form fields, FacetFiltersForm reads them straight out of FormData -
 * we only hide them and write to them as the handles move.
 *
 * The handles are built in a SHADOW ROOT on purpose, for two reasons:
 *
 *   1. Dawn's <price-range> element runs `this.querySelectorAll('input')` in its
 *      constructor and binds its own change/keydown handlers to everything it
 *      finds. Its adjustToValidValues() would then read our range inputs'
 *      (absent) data-max, get Number(null) === 0, and slam the handle back to
 *      zero on every release. Which of the two custom elements upgrades first
 *      depends on script order, so "just define ours later" is not a guarantee.
 *      A shadow root is - querySelectorAll cannot cross it.
 *   2. d2f-custom.css loads BEFORE Dawn's stylesheets, so equal-specificity ties
 *      go to Dawn. Styling inside the shadow root sidesteps that fight entirely.
 *
 * 'change' does not cross a shadow boundary (composed: false), so Dawn never
 * sees the handle commits either. 'input' does cross (composed: true), so we
 * stop it at the host - otherwise every drag pause would fire a filter request.
 */
(function () {
  if (customElements.get('d2f-price-slider')) return;

  // 24px is WCAG 2.2 SC 2.5.8's minimum target size. The constant also drives the rail
  // height and the track inset - change it in one place or the fill and handles drift apart.
  var THUMB = 24;

  var SHEET = [
    ':host { display: block; padding: 0.4rem 0 0.2rem; }',
    '.scale { display: flex; font-size: 1.2rem; line-height: 1; color: #6c7076; margin: 0 0 0.7rem; }',
    '.scale span { flex: 1 1 0; text-align: center; }',
    '.scale span:first-child { text-align: left; }',
    '.scale span:last-child { text-align: right; }',

    '.rail { position: relative; height: ' + THUMB + 'px; touch-action: manipulation;',
    '  -webkit-tap-highlight-color: transparent; }',
    /* the track is inset by half a thumb each side so that 0% and 100% land exactly
       under the handle centres - a native range keeps its thumb inside its own box */
    '.track { position: absolute; left: ' + THUMB / 2 + 'px; right: ' + THUMB / 2 + 'px;',
    '  top: 50%; height: 6px; margin-top: -3px; border-radius: 6px; background: #e7e5e1; }',
    '.fill { position: absolute; top: 0; bottom: 0; border-radius: 6px;',
    '  background: var(--d2f-accent, #FED000); }',

    'input { position: absolute; left: 0; top: 0; width: 100%; height: 100%; margin: 0;',
    '  padding: 0; background: transparent; border: none; -webkit-appearance: none;',
    '  appearance: none; pointer-events: none; }',
    'input:focus { outline: none; }',

    /* Safari/Chrome paint a default track unless both the input and the runnable
       track are reset; the thumb is the only part that takes the pointer */
    'input::-webkit-slider-runnable-track { height: ' + THUMB + 'px; background: transparent; border: none; }',
    'input::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; box-sizing: border-box;',
    '  width: ' + THUMB + 'px; height: ' + THUMB + 'px; border-radius: 50%; background: #ffffff;',
    '  border: 2px solid var(--d2f-ink, #222222); box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);',
    '  pointer-events: auto; cursor: grab; }',
    'input:active::-webkit-slider-thumb { cursor: grabbing; }',
    'input:focus-visible::-webkit-slider-thumb { outline: 2px solid var(--d2f-ink, #222222); outline-offset: 2px; }',

    'input::-moz-range-track { height: ' + THUMB + 'px; background: transparent; border: none; }',
    'input::-moz-range-thumb { box-sizing: border-box; width: ' + THUMB + 'px; height: ' + THUMB + 'px;',
    '  border-radius: 50%; background: #ffffff; border: 2px solid var(--d2f-ink, #222222);',
    '  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25); pointer-events: auto; cursor: grab; }',
    'input:focus-visible::-moz-range-thumb { outline: 2px solid var(--d2f-ink, #222222); outline-offset: 2px; }',

    '.readout { margin: 0.9rem 0 0; text-align: center; font-size: 1.3rem; font-weight: 600;',
    '  color: var(--d2f-ink, #222222); }',

    /* forced-colors strips background paint, which would leave the yellow fill
       indistinguishable from the grey track - Dawn does the same at
       component-facets.css:533 */
    '@media (forced-colors: active) {',
    '  .fill { forced-color-adjust: none; background: Highlight; }',
    '  .track { border: 1px solid CanvasText; }',
    '  input::-webkit-slider-thumb, input::-moz-range-thumb {',
    '    forced-color-adjust: none; background: ButtonFace; border-color: ButtonText; }',
    '}',
  ].join('\n');

  function toNumber(raw, fallback) {
    var n = parseFloat(String(raw == null ? '' : raw).replace(/[^0-9.\-]/g, ''));
    return isFinite(n) ? n : fallback;
  }

  class D2fPriceSlider extends HTMLElement {
    connectedCallback() {
      if (!this._built && !this.build()) return;
      // re-applied on every insertion: renderFilters() can move us around
      this.markHost();
    }

    markHost() {
      var priceRange = this.closest('price-range');
      if (priceRange) priceRange.classList.add('d2f-prange-on');
      var facet = this.closest('.js-filter');
      if (facet) facet.classList.add('d2f-prange-on');
    }

    build() {
      var fields = this.closest('price-range');
      this.gte = fields ? fields.querySelector('input[name$=".gte"]') : null;
      this.lte = fields ? fields.querySelector('input[name$=".lte"]') : null;
      if (!this.gte || !this.lte) return false;

      this.rangeMin = Math.floor(toNumber(this.dataset.min, 0));
      this.rangeMax = Math.ceil(toNumber(this.dataset.max, 0));
      // nothing to drag: every product the same price, or no priced products at all
      if (!(this.rangeMax > this.rangeMin)) return false;

      this.symbol = this.dataset.symbol || '';
      this.locale = document.documentElement.lang || 'en-GB';

      var root = this.attachShadow({ mode: 'open' });
      var style = document.createElement('style');
      style.textContent = SHEET;
      root.appendChild(style);

      var scale = document.createElement('div');
      scale.className = 'scale';
      scale.setAttribute('aria-hidden', 'true');
      [this.rangeMin, (this.rangeMin + this.rangeMax) / 2, this.rangeMax].forEach(
        function (v) {
          var span = document.createElement('span');
          span.textContent = this.money(v);
          scale.appendChild(span);
        }.bind(this)
      );
      root.appendChild(scale);

      var rail = document.createElement('div');
      rail.className = 'rail';
      var track = document.createElement('div');
      track.className = 'track';
      this.fill = document.createElement('div');
      this.fill.className = 'fill';
      track.appendChild(this.fill);
      rail.appendChild(track);

      // each handle rounds TOWARDS the end it has to be able to reach: rangeMin is floored
      // and rangeMax ceiled, so only floor/ceil let an untouched handle sit exactly on its
      // own bound - which is what keeps onCommit()'s "send no filter at full stretch" true
      this.minEl = this.makeHandle(this.dataset.gte, this.rangeMin, this.dataset.labelMin || 'From', Math.floor);
      this.maxEl = this.makeHandle(this.dataset.lte, this.rangeMax, this.dataset.labelMax || 'To', Math.ceil);
      rail.appendChild(this.minEl);
      rail.appendChild(this.maxEl);
      root.appendChild(rail);

      this.readout = document.createElement('p');
      this.readout.className = 'readout';
      this.readout.setAttribute('aria-hidden', 'true'); // the handles already announce their values
      root.appendChild(this.readout);

      this.track = track;
      this.bind(rail);
      this.paint();
      this._built = true;
      return true;
    }

    makeHandle(rawValue, fallback, label, round) {
      var el = document.createElement('input');
      el.type = 'range';
      el.min = String(this.rangeMin);
      el.max = String(this.rangeMax);
      el.step = '1';
      var v = toNumber(rawValue, fallback);
      el.value = String(Math.max(this.rangeMin, Math.min(this.rangeMax, round(v))));
      el.setAttribute('aria-label', label);
      return el;
    }

    bind(rail) {
      var self = this;
      var onInput = this.onInput.bind(this);
      var commit = this.commit.bind(this);
      [this.minEl, this.maxEl].forEach(function (el) {
        el.addEventListener('input', onInput);
        el.addEventListener('change', commit); // idempotent final word on release
      });

      // 'input' is composed, so a raw handle event would escape the shadow root and reach
      // the facet form directly, with event.target reported as this host rather than a text
      // input - which is the check renderFilters() uses to decide whether to steal focus.
      // Stop it here; commit() re-dispatches a clean one from the gte field instead.
      this.addEventListener('input', function (event) {
        event.stopPropagation();
      });

      // clicking the bare track moves the nearer handle - without this the track
      // is dead, because only the thumbs take pointer events
      rail.addEventListener('pointerdown', function (event) {
        // thumb pointerdowns bubble through here, so this records how the gesture started
        self._fromHandle = event.target instanceof HTMLInputElement;
      });
      rail.addEventListener(
        'click',
        function (event) {
          if (self._fromHandle || event.target instanceof HTMLInputElement) return;
          var box = this.track.getBoundingClientRect();
          if (!box.width) return;
          var ratio = (event.clientX - box.left) / box.width;
          var value = Math.round(this.rangeMin + ratio * (this.rangeMax - this.rangeMin));
          value = Math.max(this.rangeMin, Math.min(this.rangeMax, value));
          var toMin = Math.abs(value - this.minEl.valueAsNumber);
          var toMax = Math.abs(value - this.maxEl.valueAsNumber);
          // a tie means the handles are piled on one value - break it by which way
          // the shopper clicked, or the click just bounces off
          var nearer =
            toMin === toMax
              ? value > this.minEl.valueAsNumber
                ? this.maxEl
                : this.minEl
              : toMin < toMax
              ? this.minEl
              : this.maxEl;
          nearer.value = String(value);
          this.onInput();
        }.bind(this)
      );
    }

    onInput(event) {
      // handles may meet but never cross: whichever one the shopper is moving
      // gets stopped at the other, rather than shoving it along
      if (this.minEl.valueAsNumber > this.maxEl.valueAsNumber) {
        var moved = event && event.target;
        if (moved === this.maxEl) {
          this.maxEl.value = this.minEl.value;
        } else {
          this.minEl.value = this.maxEl.value;
        }
      }
      this.paint();
      this.commit();
    }

    commit() {
      var lo = this.lowValue();
      var hi = this.highValue();

      // leave a field empty when that end is at full stretch, so Shopify is not
      // handed a redundant filter.v.price param (and no pointless "£0 - £1,300" pill)
      this.gte.value = lo > this.rangeMin ? String(lo) : '';
      this.lte.value = hi < this.rangeMax ? String(hi) : '';

      // keep Dawn's own clamp attributes coherent with what we just wrote
      this.gte.setAttribute('data-max', this.lte.value || String(this.rangeMax));
      this.lte.setAttribute('data-min', this.gte.value || String(this.rangeMin));

      // dispatched from the text input, not the handle: FacetFiltersForm.renderFilters()
      // checks event.target.getAttribute('type') === 'text' to decide not to steal focus
      this.gte.dispatchEvent(new Event('input', { bubbles: true }));
    }

    lowValue() {
      return Math.min(this.minEl.valueAsNumber, this.maxEl.valueAsNumber);
    }

    highValue() {
      return Math.max(this.minEl.valueAsNumber, this.maxEl.valueAsNumber);
    }

    paint() {
      var span = this.rangeMax - this.rangeMin;
      var lo = this.lowValue();
      var hi = this.highValue();
      this.fill.style.left = ((lo - this.rangeMin) / span) * 100 + '%';
      this.fill.style.right = 100 - ((hi - this.rangeMin) / span) * 100 + '%';
      this.readout.textContent = this.money(lo) + ' – ' + this.money(hi);
      this.minEl.setAttribute('aria-valuetext', this.money(this.minEl.valueAsNumber));
      this.maxEl.setAttribute('aria-valuetext', this.money(this.maxEl.valueAsNumber));
      // when both handles sit in the right-hand half they can overlap; lift the
      // lower one above so it stays grabbable
      this.minEl.style.zIndex = this.minEl.valueAsNumber > (this.rangeMin + this.rangeMax) / 2 ? '4' : '2';
      this.maxEl.style.zIndex = '3';
    }

    money(value) {
      return this.symbol + Math.round(value).toLocaleString(this.locale);
    }
  }

  customElements.define('d2f-price-slider', D2fPriceSlider);
})();
